/**
 * Store Locator — interactive map + list view of retailer locations.
 *
 * Data is parsed from a child <script type="application/json" data-store-locator-data>
 * block rendered server-side by sections/store-locator.liquid. Mapbox GL JS is
 * lazy-loaded after the list is rendered so the list is interactive on first paint.
 *
 * Custom element name: <store-locator-component> (matches the codebase
 * convention of `-component` suffixed elements that extend Component).
 *
 * Rendering model:
 *   - Stores ship as a single GeoJSON source (`STORES_SOURCE_ID`) with
 *     Mapbox-native clustering enabled. Three layers consume that source:
 *     a clusters circle layer, a cluster-count symbol layer, and an
 *     unclustered-point circle layer. We do NOT create one DOM marker per
 *     store — clustering is handled entirely by the GL JS runtime.
 *   - The list reflects what is currently inside the map viewport,
 *     sorted by distance to the map center, capped at MAX_RESULTS.
 *     A debounced `moveend` handler keeps the list in sync.
 *   - Retailer filter and viewport filter compose: a store has to match
 *     the chosen retailer (if any) AND fall inside the bounds.
 *
 * @typedef {Object} Retailer
 * @property {string} handle
 * @property {string} name
 * @property {string|null} logoUrl - Square brand mark (map pins, popups, chips).
 * @property {string|null} fullLogoUrl - Full wordmark (sidebar result list).
 * @property {string|null} brandColor
 *
 * @typedef {Object} Address
 * @property {string} line1
 * @property {string} [line2]
 * @property {string} city
 * @property {string} state
 * @property {string} postalCode
 * @property {string} country
 *
 * @typedef {Object} Coords
 * @property {number} lat
 * @property {number} lon
 *
 * @typedef {Object} Store
 * @property {string} id
 * @property {string} handle
 * @property {string} displayName
 * @property {Retailer} retailer
 * @property {Address} address
 * @property {Coords} coords
 * @property {string} [phone]
 * @property {string} [hours]
 * @property {string} [sourceUrl]
 */

import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';
import { storefrontQuery } from '@theme/storefront-api';

const MAPBOX_JS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.21.0/mapbox-gl.js';
const MAX_RESULTS = 25;
const EARTH_RADIUS_KM = 6371;
const KM_TO_MILES = 0.621371;

const STORES_SOURCE_ID = 'store-locator-stores';
const CLUSTERS_LAYER_ID = 'store-locator-clusters';
const CLUSTER_COUNT_LAYER_ID = 'store-locator-cluster-count';
const POINTS_LAYER_ID = 'store-locator-points';
const POINTS_ICON_LAYER_ID = 'store-locator-points-icon';
const MOVEEND_DEBOUNCE_MS = 150;
const FOCUS_ZOOM = 14;
const DEFAULT_BRAND_COLOR = '#4B2D28'; // brand brown — Mapbox paint can't read CSS vars

/** @type {Promise<any>|null} */
let mapboxLoadPromise = null;

/**
 * Lazy-load Mapbox GL JS once per page.
 *
 * @returns {Promise<any>}
 */
function loadMapbox() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  // @ts-ignore
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
  if (mapboxLoadPromise) return mapboxLoadPromise;

  mapboxLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = MAPBOX_JS_URL;
    script.async = true;
    script.onload = () => {
      // @ts-ignore
      if (window.mapboxgl) resolve(window.mapboxgl);
      else reject(new Error('Mapbox failed to load'));
    };
    script.onerror = () => reject(new Error('Mapbox script error'));
    document.head.appendChild(script);
  });

  return mapboxLoadPromise;
}

/**
 * Great-circle distance between two points (Haversine), returned in miles.
 *
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number} miles
 */
function haversineMiles(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c * KM_TO_MILES;
}

/**
 * Trailing-edge debouncer. The returned function ignores rapid calls and
 * only invokes `fn` once activity quiets for `wait` ms.
 *
 * @template {(...args: any[]) => any} F
 * @param {F} fn
 * @param {number} wait
 * @returns {(...args: Parameters<F>) => void}
 */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout>|null} */
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, wait);
  };
}

/**
 * Minimal HTML escape.
 *
 * @param {string|number|null|undefined} value
 * @returns {string}
 */
function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Format an address for display (single line, comma-separated).
 *
 * @param {Address} addr
 * @returns {string}
 */
function formatAddressLine(addr) {
  const parts = [addr.line1, addr.line2, `${addr.city}, ${addr.state} ${addr.postalCode}`].filter(
    (p) => p && String(p).trim().length > 0
  );
  return parts.join(', ');
}

/**
 * Format hours preserving newlines for HTML display.
 *
 * @param {string} hours
 * @returns {string}
 */
function formatHoursHtml(hours) {
  return escapeHtml(hours).replace(/\n/g, '<br>');
}

/**
 * Build a Google Maps directions URL for a store.
 *
 * @param {Store} store
 * @returns {string}
 */
function directionsUrl(store) {
  return `https://www.google.com/maps/dir/?api=1&destination=${store.coords.lat},${store.coords.lon}`;
}

/**
 * Format a US phone number as `(XXX) XXX-XXXX`. Strips a leading country-code
 * `1` if present. Falls back to the raw input if the digit count isn't 10.
 *
 * @param {string} raw
 * @returns {string}
 */
function formatUsPhone(raw) {
  if (!raw) return '';
  let digits = String(raw).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Render an `<svg><use>` reference into the bundled sprite.
 *
 * @param {string} spriteUrl
 * @param {string} id
 * @returns {string}
 */
function spriteIcon(spriteUrl, id) {
  return `<svg class="store-locator__icon" aria-hidden="true" focusable="false"><use href="${escapeHtml(spriteUrl)}#${escapeHtml(id)}"></use></svg>`;
}

/* ---------- HTML builders ----------
   Pure, module-scope, no `this`. Kept out of the class to keep the
   component itself focused on state + map wiring. */

/**
 * @param {Retailer} retailer
 * @returns {string}
 */
function chipHtml(retailer) {
  const color = retailer.brandColor ? escapeHtml(retailer.brandColor) : 'currentColor';
  const icon = retailer.logoUrl
    ? `<img class="store-locator__chip-icon" src="${escapeHtml(retailer.logoUrl)}" alt="" loading="lazy">`
    : '';
  return `
    <button
      type="button"
      class="store-locator__chip"
      data-retailer="${escapeHtml(retailer.handle)}"
      style="--chip-color: ${color};"
      aria-pressed="false"
      on:click="/onChipClick/${encodeURIComponent(retailer.handle)}"
    >${icon}<span>${escapeHtml(retailer.name)}</span></button>
  `;
}

/**
 * @param {Store & { _distance?: number }} store
 * @param {string} spriteUrl
 * @returns {string}
 */
function cardHtml(store, spriteUrl) {
  const dist = store._distance != null ? `${store._distance.toFixed(1)} mi` : '';
  const retailerName = escapeHtml(store.retailer?.name ?? '');
  // Sidebar shows the full wordmark when present; otherwise fall back to the
  // square mark + retailer name (the same treatment used on chips/popups).
  const retailerIcon = store.retailer?.logoUrl
    ? `<img class="store-locator__card-retailer-icon" src="${escapeHtml(store.retailer.logoUrl)}" alt="" loading="lazy">`
    : '';
  const retailerBlock = store.retailer?.fullLogoUrl
    ? `<img class="store-locator__card-logo" src="${escapeHtml(store.retailer.fullLogoUrl)}" alt="${retailerName}" loading="lazy">`
    : `<p class="store-locator__card-retailer">${retailerIcon}<span>${retailerName}</span></p>`;
  const phoneDigits = (store.phone || '').replace(/\D/g, '');
  const phone = store.phone
    ? `<a class="store-locator__card-phone" href="tel:${escapeHtml(phoneDigits || store.phone)}">
        ${spriteIcon(spriteUrl, 'icon-phone')}
        <span>${escapeHtml(formatUsPhone(store.phone))}</span>
      </a>`
    : '';
  const brandColor = store.retailer?.brandColor
    ? escapeHtml(store.retailer.brandColor)
    : 'currentColor';

  return `
    <li
      class="store-locator__card"
      data-store-id="${escapeHtml(store.id)}"
      data-store-handle="${escapeHtml(store.handle)}"
      data-retailer="${escapeHtml(store.retailer?.handle ?? '')}"
      style="--card-accent: ${brandColor};"
      on:click="/onCardClick/${encodeURIComponent(store.id)}"
      on:keydown="/onCardKeydown/${encodeURIComponent(store.id)}"
      tabindex="0"
      role="button"
      aria-label="View ${escapeHtml(store.displayName)} on map"
    >
      <div class="store-locator__card-head">
        <div class="store-locator__card-headings">
          ${retailerBlock}
          <h3 class="store-locator__card-name">${escapeHtml(store.displayName)}</h3>
        </div>
        ${dist ? `<span class="store-locator__card-distance">${escapeHtml(dist)}</span>` : ''}
      </div>
      <p class="store-locator__card-address">${escapeHtml(formatAddressLine(store.address))}</p>
      <div class="store-locator__card-actions">
        ${phone}
        <a
          class="store-locator__card-directions"
          href="${directionsUrl(store)}"
          target="_blank"
          rel="noopener noreferrer"
        >
          ${spriteIcon(spriteUrl, 'icon-directions')}
          <span>Get directions</span>
        </a>
      </div>
    </li>
  `;
}

/**
 * @param {Store} store
 * @param {string} spriteUrl
 * @returns {string}
 */
function popupHtml(store, spriteUrl) {
  const retailerIcon = store.retailer?.logoUrl
    ? `<img class="store-locator-popup__retailer-icon" src="${escapeHtml(store.retailer.logoUrl)}" alt="" loading="lazy">`
    : '';
  const phoneDigits = (store.phone || '').replace(/\D/g, '');
  const phone = store.phone
    ? `<p class="store-locator-popup__row store-locator-popup__row--phone">
        <a href="tel:${escapeHtml(phoneDigits || store.phone)}">
          ${spriteIcon(spriteUrl, 'icon-phone')}
          <span>${escapeHtml(formatUsPhone(store.phone))}</span>
        </a>
      </p>`
    : '';
  const hours = store.hours
    ? `<div class="store-locator-popup__hours">${formatHoursHtml(store.hours)}</div>`
    : '';
  const source = store.sourceUrl
    ? `<a class="store-locator-popup__source" href="${escapeHtml(store.sourceUrl)}" target="_blank" rel="noopener noreferrer">
        ${spriteIcon(spriteUrl, 'icon-external')}
        <span>Visit store page</span>
      </a>`
    : '';
  const brandColor = store.retailer?.brandColor
    ? escapeHtml(store.retailer.brandColor)
    : 'currentColor';

  return `
    <div class="store-locator-popup__inner" style="--card-accent: ${brandColor};">
      <div class="store-locator-popup__head">
        <p class="store-locator-popup__retailer">
          ${retailerIcon}
          <span>${escapeHtml(store.retailer?.name ?? '')}</span>
        </p>
        <h3 class="store-locator-popup__name">${escapeHtml(store.displayName)}</h3>
      </div>
      <p class="store-locator-popup__row">${escapeHtml(formatAddressLine(store.address))}</p>
      ${phone}
      ${hours}
      <div class="store-locator-popup__actions">
        <a
          class="store-locator-popup__directions"
          href="${directionsUrl(store)}"
          target="_blank"
          rel="noopener noreferrer"
        >
          ${spriteIcon(spriteUrl, 'icon-directions')}
          <span>Get directions</span>
        </a>
        ${source}
      </div>
    </div>
  `;
}

/**
 * @typedef {Object} Refs
 * @property {HTMLInputElement} zipInput
 * @property {HTMLButtonElement} searchBtn
 * @property {HTMLButtonElement} geolocateBtn
 * @property {HTMLElement} retailerChips
 * @property {HTMLElement} mapEl
 * @property {HTMLUListElement} listEl
 * @property {HTMLElement} emptyState
 *
 * @extends {Component<Refs>}
 */
class StoreLocator extends Component {
  requiredRefs = ['mapEl', 'listEl', 'retailerChips', 'zipInput'];

  /** @type {Store[]} */
  #stores = [];

  /** @type {Map<string, Store>} */
  #storesById = new Map();

  /** @type {string|null} */
  #retailerFilter = null;

  /** True once we've tried (or actually invoked) geolocation in this session.
   *  Gates the auto-prompt that fires on first zip-input focus so we don't
   *  re-nag if the user clicks the input multiple times. */
  #geolocatePrompted = false;

  /** Store currently "focused" — clicked in sidebar or on map. Drives the
   *  is-active highlight on the sidebar card and the locked popup state.
   *  @type {string|null} */
  #activeStoreId = null;

  /** Popup opened by hover — auto-closes on mouseleave.
   *  @type {any} */
  #hoverPopup = null;

  /** Popup opened by click — sticky until explicit close or new click.
   *  @type {any} */
  #lockedPopup = null;

  /** @type {any} */
  #map = null;

  /** @type {any} */
  #mapboxgl = null;

  /** @type {boolean} */
  #mapReady = false;

  /** @type {(() => void)|null} */
  #debouncedMoveEnd = null;

  connectedCallback() {
    super.connectedCallback();

    // Initial paint before stores load: empty chips + empty list.
    this.#renderChips();
    this.#renderList([]);

    // Kick off the data + map init in parallel-ish: stores load via
    // Storefront API; the map JS lazy-loads from the Mapbox CDN. Both
    // resolve before we install the GeoJSON source / layers.
    this.#bootstrap();
  }

  async #bootstrap() {
    try {
      this.#stores = await this.#fetchAllStores();
      this.#storesById = new Map(this.#stores.map((s) => [s.id, s]));
      this.#renderChips();
      this.#renderList(this.#fallbackStores());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[store-locator] failed to fetch stores:', err);
      return;
    }

    // Mobile (≤749px) shows the store list only — no map. Skip Mapbox
    // entirely: the list already renders from #fallbackStores() above, and
    // initializing Mapbox in a display:none container can throw. Keep this
    // breakpoint in sync with the max-width:749px rule in store-locator.liquid.
    if (window.matchMedia('(max-width: 749px)').matches) return;

    requestIdleCallback(() => {
      this.#initMap().catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[store-locator] map init failed:', err);
      });
    });
  }

  /* ---------- data ---------- */

  /**
   * Fetch every store_location metaobject via the Shopify Storefront API.
   * Paginates with 250 per page (the Storefront API maximum) until
   * pageInfo.hasNextPage is false. Drops any row without valid coords.
   *
   * @returns {Promise<Store[]>}
   */
  async #fetchAllStores() {
    const query = /* GraphQL */ `
      query StoreLocations($cursor: String) {
        metaobjects(type: "store_location", first: 250, after: $cursor) {
          nodes {
            id
            handle
            displayName: field(key: "display_name") { value }
            retailer: field(key: "retailer") {
              reference {
                ... on Metaobject {
                  handle
                  slug: field(key: "slug") { value }
                  name: field(key: "name") { value }
                  logo: field(key: "logo") {
                    reference { ... on MediaImage { image { url } } }
                  }
                  fullLogo: field(key: "full_logo") {
                    reference { ... on MediaImage { image { url } } }
                  }
                  brandColor: field(key: "brand_color") { value }
                }
              }
            }
            addressLine1: field(key: "address_line_1") { value }
            addressLine2: field(key: "address_line_2") { value }
            city: field(key: "city") { value }
            state: field(key: "state") { value }
            postalCode: field(key: "postal_code") { value }
            country: field(key: "country") { value }
            latitude: field(key: "latitude") { value }
            longitude: field(key: "longitude") { value }
            phone: field(key: "phone") { value }
            hours: field(key: "hours") { value }
            sourceUrl: field(key: "source_url") { value }
          }
          pageInfo { hasNextPage endCursor }
        }
      }
    `;

    const all = [];
    let cursor = null;
    for (let safety = 0; safety < 20; safety++) {
      const data = await storefrontQuery(query, { cursor });
      const page = data?.metaobjects;
      if (!page) break;
      for (const node of page.nodes) {
        const lat = Number(node?.latitude?.value);
        const lon = Number(node?.longitude?.value);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
        const retailerRef = node?.retailer?.reference ?? {};
        all.push({
          id: node.id,
          handle: node.handle,
          displayName: node?.displayName?.value ?? '',
          retailer: {
            handle: retailerRef?.slug?.value || retailerRef?.handle || '',
            name: retailerRef?.name?.value || '',
            logoUrl: retailerRef?.logo?.reference?.image?.url || null,
            fullLogoUrl: retailerRef?.fullLogo?.reference?.image?.url || null,
            brandColor: retailerRef?.brandColor?.value || '',
          },
          address: {
            line1: node?.addressLine1?.value || '',
            line2: node?.addressLine2?.value || '',
            city: node?.city?.value || '',
            state: node?.state?.value || '',
            postalCode: node?.postalCode?.value || '',
            country: node?.country?.value || '',
          },
          coords: { lat, lon },
          phone: node?.phone?.value || '',
          hours: node?.hours?.value || '',
          sourceUrl: node?.sourceUrl?.value || '',
        });
      }
      if (!page.pageInfo?.hasNextPage) break;
      cursor = page.pageInfo.endCursor;
    }
    return all;
  }

  /**
   * Stores after applying the retailer chip filter (no viewport filter yet).
   *
   * @returns {Store[]}
   */
  #filteredStores() {
    if (!this.#retailerFilter) return this.#stores;
    return this.#stores.filter((s) => s.retailer?.handle === this.#retailerFilter);
  }

  /**
   * Alphabetical fallback used before the map is ready / for the empty
   * default state. Capped at MAX_RESULTS.
   *
   * @returns {Store[]}
   */
  #fallbackStores() {
    return [...this.#filteredStores()]
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .slice(0, MAX_RESULTS);
  }

  /**
   * Stores currently inside the map viewport, sorted by distance to map
   * center, capped at MAX_RESULTS. Retailer filter is composed in.
   *
   * @returns {(Store & { _distance?: number })[]}
   */
  #viewportStores() {
    if (!this.#map) return this.#fallbackStores();
    const bounds = this.#map.getBounds();
    if (!bounds) return this.#fallbackStores();
    const center = this.#map.getCenter();

    const inView = this.#filteredStores().filter((s) =>
      bounds.contains([s.coords.lon, s.coords.lat])
    );

    const annotated = inView.map((s) => ({
      ...s,
      _distance: haversineMiles(center.lat, center.lng, s.coords.lat, s.coords.lon),
    }));
    annotated.sort((a, b) => (a._distance ?? 0) - (b._distance ?? 0));
    return annotated.slice(0, MAX_RESULTS);
  }

  /**
   * Stores sorted by distance to an arbitrary point, used when there's no map
   * (phones) so zip search / geolocate still re-order the list by proximity.
   * Retailer filter is composed in. Capped at MAX_RESULTS.
   *
   * @param {number} lat
   * @param {number} lon
   * @returns {(Store & { _distance?: number })[]}
   */
  #nearestStores(lat, lon) {
    const annotated = this.#filteredStores().map((s) => ({
      ...s,
      _distance: haversineMiles(lat, lon, s.coords.lat, s.coords.lon),
    }));
    annotated.sort((a, b) => (a._distance ?? 0) - (b._distance ?? 0));
    return annotated.slice(0, MAX_RESULTS);
  }

  /* ---------- filter chips ---------- */

  #renderChips() {
    /** @type {Map<string, Retailer>} */
    const byHandle = new Map();
    for (const store of this.#stores) {
      const r = store.retailer;
      if (r?.handle && !byHandle.has(r.handle)) byHandle.set(r.handle, r);
    }
    const retailers = Array.from(byHandle.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    this.refs.retailerChips.innerHTML = retailers.map(chipHtml).join('');
    // Filter chips are only meaningful when there's more than one chain.
    this.refs.retailerChips.hidden = retailers.length < 2;
  }

  /* ---------- list ---------- */

  /**
   * @param {(Store & { _distance?: number })[]} stores
   */
  #renderList(stores) {
    const { listEl, emptyState } = this.refs;

    if (!stores.length) {
      listEl.innerHTML = '';
      emptyState.hidden = false;
      return;
    }
    emptyState.hidden = true;

    const spriteUrl = this.#spriteUrl();
    listEl.innerHTML = stores.map((s) => cardHtml(s, spriteUrl)).join('');
    // List was just rebuilt — reapply the active highlight if it's in view.
    if (this.#activeStoreId) {
      const card = listEl.querySelector(
        `.store-locator__card[data-store-id="${CSS.escape(this.#activeStoreId)}"]`
      );
      if (card) card.classList.add('is-active');
    }
  }

  /** Sprite URL plumbed through from the Liquid section via data attribute. */
  #spriteUrl() {
    return this.dataset.spriteUrl || '';
  }

  /* ---------- map ---------- */

  async #initMap() {
    const token = this.dataset.mapboxToken;
    if (!token) {
      // eslint-disable-next-line no-console
      console.warn('[store-locator] no Mapbox token configured');
      return;
    }

    const mapboxgl = await loadMapbox();
    mapboxgl.accessToken = token;
    this.#mapboxgl = mapboxgl;

    /** @type {[number, number]} */
    let center = [-98.35, 39.5];
    try {
      const parsed = JSON.parse(this.dataset.defaultCenter || '[-98.35, 39.5]');
      if (Array.isArray(parsed) && parsed.length === 2) {
        center = [Number(parsed[0]), Number(parsed[1])];
      }
    } catch {}
    const zoom = Number(this.dataset.defaultZoom) || 4;
    const style = this.dataset.mapboxStyle || 'mapbox://styles/mapbox/standard';

    this.#map = new mapboxgl.Map({
      container: this.refs.mapEl,
      style,
      center,
      zoom,
      attributionControl: true,
    });
    this.#map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');

    this.#debouncedMoveEnd = debounce(() => {
      this.#renderList(this.#viewportStores());
    }, MOVEEND_DEBOUNCE_MS);
    this.#map.on('moveend', this.#debouncedMoveEnd);

    this.#map.on('load', () => {
      this.#mapReady = true;
      this.#installStoresLayer();
      // First sync: the load event doesn't fire moveend, so kick the list now.
      this.#renderList(this.#viewportStores());
      // If the user has already granted geolocation permission (in a prior
      // visit), silently fly to their location. Skipped when state is
      // 'prompt' — we don't want to surprise first-time visitors with a
      // permission dialog on page load. They get prompted on zip focus
      // instead.
      this.#autoCenterIfPermitted();
    });
  }

  /**
   * One-shot: silently center the map on the user's current location IF
   * they've granted permission previously. No-op for 'prompt' / 'denied' /
   * Permissions-API-less browsers.
   */
  #autoCenterIfPermitted() {
    if (this.#geolocatePrompted) return;
    if (!navigator.geolocation || !navigator.permissions?.query) return;
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (result.state === 'granted') this.onGeolocate();
      })
      .catch(() => {
        /* silent — best-effort feature */
      });
  }

  /**
   * Add the GeoJSON source + cluster/point layers to the map. Wires
   * click handlers for cluster expansion and individual-point popups.
   */
  #installStoresLayer() {
    if (!this.#map || !this.#mapboxgl) return;

    this.#map.addSource(STORES_SOURCE_ID, {
      type: 'geojson',
      data: this.#buildGeoJson(this.#filteredStores()),
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });

    // Cluster palette — Mapbox paint expressions can't read CSS variables,
    // so these are intentionally hard-coded hex. Chosen to feel close to the
    // brand greens/browns. Designers: tweak these three pairs to taste.
    //   small (< 10 points): pale brand cream-green
    //   medium (10–24):       caramel mid-tone
    //   large (>= 25):         brand brown
    this.#map.addLayer({
      id: CLUSTERS_LAYER_ID,
      type: 'circle',
      source: STORES_SOURCE_ID,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#BBC74B', // small (< 10) — flavor-original (brand accent)
          10,
          '#AD7752', // medium (10–24) — brand caramel
          25,
          '#4B2D28', // large (25+) — brand brown
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          16, // small
          10,
          22, // medium
          25,
          28, // large
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FAF0DA', // brand cream
        'circle-opacity': 0.9,
      },
    });

    this.#map.addLayer({
      id: CLUSTER_COUNT_LAYER_ID,
      type: 'symbol',
      source: STORES_SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-size': 13,
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-allow-overlap': true,
      },
      paint: {
        // Use brand-brown for small (light bg) and cream for darker bg.
        'text-color': [
          'step',
          ['get', 'point_count'],
          '#4B2D28', // small cluster — dark text on light bg
          10,
          '#FAF0DA', // medium — light text on caramel
          25,
          '#FAF0DA', // large — light text on brown
        ],
      },
    });

    this.#map.addLayer({
      id: POINTS_LAYER_ID,
      type: 'circle',
      source: STORES_SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      paint: {
        // Per-feature brand color (set in #buildGeoJson), falling back to brown.
        'circle-color': ['coalesce', ['get', 'brandColor'], DEFAULT_BRAND_COLOR],
        'circle-radius': 9,
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#FAF0DA', // brand cream
      },
    });

    // Load each unique retailer's logo into Mapbox's image atlas and add a
    // symbol layer on top of the circle so the marker reads as "Sprouts pin"
    // / "Whole Foods pin" at a glance.
    this.#loadRetailerIcons().then(() => this.#installRetailerSymbolLayer());

    // Cluster click → expand zoom (standard Mapbox recipe).
    this.#map.on('click', CLUSTERS_LAYER_ID, (e) => {
      const features = this.#map.queryRenderedFeatures(e.point, {
        layers: [CLUSTERS_LAYER_ID],
      });
      const clusterId = features[0]?.properties?.cluster_id;
      if (clusterId == null) return;
      const source = this.#map.getSource(STORES_SOURCE_ID);
      if (!source?.getClusterExpansionZoom) return;
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        this.#map.easeTo({
          center: features[0].geometry.coordinates,
          zoom,
        });
      });
    });

    // Unclustered point click → fly to + sidebar highlight + sticky popup.
    // Both the circle and the icon-overlay layer fire this.
    const onPointClick = (e) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const storeId = feature.properties?.id;
      if (!storeId) return;
      this.#focusStore(String(storeId));
    };
    this.#map.on('click', POINTS_LAYER_ID, onPointClick);
    this.#map.on('click', POINTS_ICON_LAYER_ID, onPointClick);

    // Hover → transient popup (closes on mouseleave). Doesn't fly the map
    // or set the active sidebar row — those happen on click.
    const onPointEnter = (e) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const storeId = feature.properties?.id;
      const store = storeId ? this.#storesById.get(String(storeId)) : null;
      if (!store) return;
      // Don't replace a sticky click-popup with a hover preview.
      if (this.#lockedPopup) return;
      const coords = feature.geometry?.coordinates?.slice();
      this.#openHoverPopup(store, coords || [store.coords.lon, store.coords.lat]);
    };
    const onPointLeave = () => {
      if (this.#hoverPopup) {
        this.#hoverPopup.remove();
        this.#hoverPopup = null;
      }
    };

    // Cursor affordances.
    const setPointer = () => {
      this.#map.getCanvas().style.cursor = 'pointer';
    };
    const clearPointer = () => {
      this.#map.getCanvas().style.cursor = '';
    };
    this.#map.on('mouseenter', CLUSTERS_LAYER_ID, setPointer);
    this.#map.on('mouseleave', CLUSTERS_LAYER_ID, clearPointer);
    this.#map.on('mouseenter', POINTS_LAYER_ID, (e) => {
      setPointer();
      onPointEnter(e);
    });
    this.#map.on('mouseleave', POINTS_LAYER_ID, () => {
      clearPointer();
      onPointLeave();
    });
    this.#map.on('mouseenter', POINTS_ICON_LAYER_ID, (e) => {
      setPointer();
      onPointEnter(e);
    });
    this.#map.on('mouseleave', POINTS_ICON_LAYER_ID, () => {
      clearPointer();
      onPointLeave();
    });
  }

  /**
   * Load each unique retailer's logo into Mapbox's image atlas, keyed by
   * retailer handle (which is what the icon-image expression reads from
   * the feature properties). Skips retailers without a logo URL. Failures
   * are non-fatal — the underlying circle pin still renders.
   *
   * @returns {Promise<void>}
   */
  async #loadRetailerIcons() {
    if (!this.#map || !this.#mapboxgl) return;
    /** @type {Map<string, string>} */
    const byHandle = new Map();
    for (const store of this.#stores) {
      const r = store.retailer;
      if (r?.handle && r?.logoUrl && !byHandle.has(r.handle)) {
        byHandle.set(r.handle, r.logoUrl);
      }
    }

    const tasks = [];
    for (const [handle, url] of byHandle) {
      if (this.#map.hasImage(handle)) continue;
      // Force the Shopify CDN to serve a known 64×64 raster so icon-size
      // scaling is predictable across retailers (logos in the metaobject
      // can be any source dimension).
      const sizedUrl = url.includes('?')
        ? `${url}&width=64&height=64`
        : `${url}?width=64&height=64`;
      tasks.push(
        new Promise((resolve) => {
          this.#map.loadImage(sizedUrl, (err, image) => {
            if (err || !image) {
              // eslint-disable-next-line no-console
              console.warn(`[store-locator] failed to load icon for ${handle}:`, err);
              resolve();
              return;
            }
            if (!this.#map.hasImage(handle)) {
              this.#map.addImage(handle, image);
            }
            resolve();
          });
        })
      );
    }
    await Promise.all(tasks);
  }

  /**
   * Add the symbol layer that draws retailer icons over the circle pins.
   * Safe to call after #loadRetailerIcons has resolved; the `icon-image`
   * expression will simply render nothing for handles missing from the
   * atlas (Mapbox returns "icon-image not found" warnings — quiet, not
   * fatal).
   */
  #installRetailerSymbolLayer() {
    if (!this.#map || this.#map.getLayer(POINTS_ICON_LAYER_ID)) return;
    this.#map.addLayer({
      id: POINTS_ICON_LAYER_ID,
      type: 'symbol',
      source: STORES_SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': ['get', 'retailerHandle'],
        // Source images are forced to 64×64 in #loadRetailerIcons; this
        // renders them at ~24px — a bit bigger than maki POI icons (~11–15px)
        // so the chain logo reads clearly without dominating the map.
        'icon-size': 0.375,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
  }

  /**
   * Convert a list of stores into a Mapbox-compatible GeoJSON
   * FeatureCollection. Keeps properties small — full store data lives
   * in `#storesById` and is looked up by id at click time.
   *
   * @param {Store[]} stores
   * @returns {{ type: 'FeatureCollection', features: any[] }}
   */
  #buildGeoJson(stores) {
    return {
      type: 'FeatureCollection',
      features: stores.map((store) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [store.coords.lon, store.coords.lat],
        },
        properties: {
          id: store.id,
          handle: store.handle,
          retailerHandle: store.retailer?.handle ?? '',
          brandColor: store.retailer?.brandColor || DEFAULT_BRAND_COLOR,
        },
      })),
    };
  }

  /**
   * Push fresh GeoJSON into the live source so clusters re-form.
   * No-op until the source has been installed.
   */
  #refreshSource() {
    if (!this.#mapReady || !this.#map) return;
    const source = this.#map.getSource(STORES_SOURCE_ID);
    if (!source) return;
    source.setData(this.#buildGeoJson(this.#filteredStores()));
  }

  /**
   * Transient hover popup — replaces any existing hover popup, leaves any
   * locked (click-opened) popup alone. Closes via #closeHoverPopup() on
   * mouseleave.
   *
   * @param {Store} store
   * @param {[number, number]} lngLat
   */
  #openHoverPopup(store, lngLat) {
    if (!this.#mapboxgl || !this.#map) return;
    if (this.#lockedPopup) return; // locked popup wins
    if (this.#hoverPopup) {
      this.#hoverPopup.remove();
      this.#hoverPopup = null;
    }
    this.#hoverPopup = new this.#mapboxgl.Popup({
      offset: 16,
      closeButton: false,
      closeOnClick: false,
      className: 'store-locator-popup',
    })
      .setLngLat(lngLat)
      .setHTML(popupHtml(store, this.#spriteUrl()))
      .addTo(this.#map);
  }

  /**
   * Sticky popup opened by a click (either on a map marker or on a sidebar
   * card). Replaces any existing popup. Closes only via the popup's close
   * button or when another store is focused.
   *
   * @param {Store} store
   * @param {[number, number]} lngLat
   */
  #openLockedPopup(store, lngLat) {
    if (!this.#mapboxgl || !this.#map) return;
    if (this.#hoverPopup) {
      this.#hoverPopup.remove();
      this.#hoverPopup = null;
    }
    if (this.#lockedPopup) {
      this.#lockedPopup.remove();
      this.#lockedPopup = null;
    }
    const popup = new this.#mapboxgl.Popup({
      offset: 16,
      closeButton: true,
      closeOnClick: false,
      className: 'store-locator-popup',
    })
      .setLngLat(lngLat)
      .setHTML(popupHtml(store, this.#spriteUrl()))
      .addTo(this.#map);
    popup.on('close', () => {
      if (this.#lockedPopup === popup) {
        this.#lockedPopup = null;
        this.#setActive(null);
      }
    });
    this.#lockedPopup = popup;
  }

  /**
   * Mark a store as the currently-focused one in the sidebar. Updates
   * .is-active classes on rendered cards and scrolls the active card into
   * view. Pass `null` to clear.
   *
   * @param {string|null} storeId
   */
  #setActive(storeId) {
    this.#activeStoreId = storeId;
    const { listEl } = this.refs;
    if (!listEl) return;
    for (const card of listEl.querySelectorAll('.store-locator__card')) {
      card.classList.toggle('is-active', card.dataset.storeId === storeId);
    }
    if (storeId) {
      const active = listEl.querySelector(
        `.store-locator__card[data-store-id="${CSS.escape(storeId)}"]`
      );
      if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  /* ---------- refresh ---------- */

  /**
   * Re-sync map + list to current filter state. The list update is
   * conditional on map readiness: if the map isn't up yet we fall back
   * to the alphabetical first MAX_RESULTS; otherwise we go through the
   * viewport pipeline (and a follow-up `moveend` will refine after any
   * subsequent fly).
   */
  #refresh() {
    this.#refreshSource();
    this.#renderList(this.#mapReady ? this.#viewportStores() : this.#fallbackStores());
  }

  /* ---------- event handlers (referenced via on:* attrs) ---------- */

  /** @param {Event} event */
  onSearch(event) {
    event.preventDefault();
    const zip = (this.refs.zipInput.value || '').trim();
    if (!zip) return;
    this.#geocodeZip(zip).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[store-locator] geocode failed:', err);
    });
  }

  onGeolocate() {
    if (!navigator.geolocation) return;
    this.#geolocatePrompted = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lon } = pos.coords;
        this.#focusPoint(lat, lon, 9);

        // Best-effort: reverse-geocode the fix to a US postcode and prefill
        // the zip field. Skipped if the user already typed something (we
        // don't want to clobber input during the async hop) or if the fix
        // is outside the US (country=us filter returns nothing).
        this.#reverseGeocodePostcode(lat, lon)
          .then((postcode) => {
            const input = this.refs.zipInput;
            if (postcode && input && !input.value) {
              input.value = postcode;
            }
          })
          .catch(() => {
            /* silent — the map fly already happened, no need to surface */
          });
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.warn('[store-locator] geolocation denied:', err);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  }

  /**
   * Fires on first focus of the zip input. Auto-triggers the geolocation
   * flow — silent fetch when permission is already granted, native browser
   * prompt when it's "prompt", no-op when denied. One-shot per page load
   * via the #geolocatePrompted flag so refocusing doesn't re-nag.
   */
  onZipFocus() {
    if (this.#geolocatePrompted) return;
    if (!navigator.geolocation) return;

    // Permissions API lets us skip the call entirely when the user has
    // already said no — avoids logging a useless warning. Older browsers
    // (no navigator.permissions) just fall through to the geolocate call;
    // the OS-level cache handles the "already denied" case there.
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          if (result.state === 'denied') {
            this.#geolocatePrompted = true;  // don't keep checking
            return;
          }
          this.onGeolocate();
        })
        .catch(() => {
          this.onGeolocate();
        });
    } else {
      this.onGeolocate();
    }
  }

  /**
   * @param {string} handle
   * @param {Event} event
   */
  onChipClick(handle, event) {
    const target = /** @type {HTMLButtonElement} */ (event.currentTarget || event.target);
    if (this.#retailerFilter === handle) {
      this.#retailerFilter = null;
    } else {
      this.#retailerFilter = handle;
    }
    // Update aria-pressed on all chips.
    for (const chip of this.refs.retailerChips.querySelectorAll('.store-locator__chip')) {
      const isActive = chip.getAttribute('data-retailer') === this.#retailerFilter;
      chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      chip.classList.toggle('is-active', isActive);
    }
    if (!this.#retailerFilter && target) target.setAttribute('aria-pressed', 'false');
    this.#refresh();
  }

  /**
   * @param {string} storeId  URL-encoded by the on:click template (the
   *   component framework treats `/` as a path delimiter, so store gids like
   *   `gid://shopify/Metaobject/123` have to round-trip through
   *   encodeURIComponent / decodeURIComponent).
   */
  onCardClick(storeId) {
    this.#focusStore(decodeURIComponent(storeId));
  }

  /**
   * @param {string} storeId  Same encoding caveat as onCardClick.
   * @param {KeyboardEvent} event
   */
  onCardKeydown(storeId, event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#focusStore(decodeURIComponent(storeId));
    }
  }

  /* ---------- helpers that touch the map ---------- */

  /**
   * Mark a store as the active selection: highlight it in the sidebar,
   * fly the map to it, and open a sticky popup on arrival.
   *
   * @param {string} storeId
   */
  #focusStore(storeId) {
    const store = this.#storesById.get(storeId);
    if (!store) return;
    this.#setActive(storeId);
    this.#flyTo(store.coords, FOCUS_ZOOM);
    if (!this.#map) return;

    const openOnArrival = () => {
      this.#map.off('moveend', openOnArrival);
      this.#openLockedPopup(store, [store.coords.lon, store.coords.lat]);
    };
    this.#map.on('moveend', openOnArrival);
  }

  /**
   * @param {Coords} coords
   * @param {number} [zoom]
   */
  #flyTo(coords, zoom = 10) {
    if (!this.#map) return;
    this.#map.flyTo({
      center: [coords.lon, coords.lat],
      zoom,
      essential: true,
    });
  }

  /**
   * Recenter on a point. With a map (desktop), fly there and let `moveend`
   * refresh the list. Without a map (phones), re-sort the list by proximity to
   * the point so zip search and geolocate still work.
   *
   * @param {number} lat
   * @param {number} lon
   * @param {number} [zoom]
   */
  #focusPoint(lat, lon, zoom = 9) {
    if (this.#map) {
      this.#flyTo({ lat, lon }, zoom);
      // moveend will refresh the list.
    } else {
      this.#renderList(this.#nearestStores(lat, lon));
    }
  }

  /**
   * Geocode a US zip code via the Mapbox Geocoding API (transient query,
   * not stored — this falls under the temporary endpoint usage policy).
   *
   * @param {string} zip
   */
  async #geocodeZip(zip) {
    const token = this.dataset.mapboxToken;
    if (!token) return;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(zip)}.json?access_token=${encodeURIComponent(token)}&country=us&types=postcode&limit=1`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Geocoding HTTP ${res.status}`);
    const data = await res.json();
    const feature = data?.features?.[0];
    if (!feature?.center) return;
    const [lon, lat] = feature.center;
    this.#focusPoint(lat, lon, 9);
  }

  /**
   * Reverse-geocode a lat/lon to its US postcode via Mapbox. Best-effort:
   * returns null on any failure (no token, network blip, non-US fix, no
   * postcode for the area). Same transient-query policy as #geocodeZip.
   *
   * @param {number} lat
   * @param {number} lon
   * @returns {Promise<string|null>}
   */
  async #reverseGeocodePostcode(lat, lon) {
    const token = this.dataset.mapboxToken;
    if (!token) return null;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${encodeURIComponent(token)}&country=us&types=postcode&limit=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const feature = data?.features?.[0];
    return feature?.text || null;
  }

}

if (!customElements.get('store-locator-component')) {
  customElements.define('store-locator-component', StoreLocator);
}
